/**
 * A race's settings in a few words, composed from the texts in ru.ts: the card in the room,
 * the toast for the others, the subtitle of the results.
 */
import type { RaceSettings } from '@cube-rush/shared';
import { ru } from '../../i18n/ru';

/**
 * How long the race is: «10 уровней», «5 минут»; short: «10», «5 мин». A length that is not a
 * whole number of minutes (a server set up for tests) is given in seconds: «50 с».
 */
export function raceLength(settings: RaceSettings, short = false): string {
  if (settings.format === 'levels') {
    return short ? String(settings.levelCount) : ru.race.levels(settings.levelCount);
  }
  return durationText(settings.durationSec, short);
}

/** A race against the clock's length: «5 минут», short «5 мин», or «50 с». */
export function durationText(seconds: number, short = false): string {
  if (seconds % 60 !== 0) return ru.race.seconds(seconds);
  const minutes = seconds / 60;
  return short ? ru.race.minutesShort(minutes) : ru.race.minutes(minutes);
}

/** Spaces that never break a line: an icon stays with its word, a number with its unit. */
function bound(text: string): string {
  return text.replace(/ /g, '\u00a0');
}

/**
 * The two halves of the summary: «🏁 10 уровней» and «📈 Нарастающая», each kept on one line
 * (no-break spaces inside; a line may break only around the dot between them).
 */
export function raceSummaryParts(settings: RaceSettings, short = false): [string, string] {
  const { format, difficulty } = settings;
  return [
    bound(`${ru.race.formatIcons[format]} ${raceLength(settings, short)}`),
    bound(`${ru.race.difficultyIcons[difficulty]} ${ru.race.difficulties[difficulty]}`),
  ];
}

/**
 * «🏁 10 уровней · 📈 Нарастающая», «⏱️ 5 минут · 🔥 Сложно»; short (one line on a phone held
 * sideways): «🏁 10 · 📈 Нарастающая», «⏱️ 5 мин · 🔥 Сложно».
 */
export function raceSummary(settings: RaceSettings, short = false): string {
  return raceSummaryParts(settings, short).join(' · ');
}

/** The same in plain words, for screen readers: «10 уровней, Нарастающая». */
export function raceSummaryText(settings: RaceSettings): string {
  return `${raceLength(settings)}, ${ru.race.difficulties[settings.difficulty]}`;
}
